export const PASSWORD_MIN_LENGTH = 8

export type PasswordStrength = "Weak" | "Good" | "Strong"

// Unicode classes, not [A-Z]/[a-z]/[0-9]: the backend tests case with
// str.isupper/islower, so "Übung123" satisfies the rule server-side and an
// ASCII class here would reject a password the API accepts.
const UPPERCASE = /\p{Lu}/u
const LOWERCASE = /\p{Ll}/u
const DIGIT = /\p{Nd}/u
const SYMBOL = /[^\p{L}\p{N}]/u

// Django counts code points; `String.length` counts UTF-16 units, so an astral
// character would read as two here and pass a length the server then refuses.
const codePointLength = (value: string) => Array.from(value).length

/**
 * The unmet halves of the server's password policy, in the backend's order.
 *
 * Empty is not a policy failure — the field's `required` rule owns that, and
 * complaining twice about an untouched field is noise.
 */
export function passwordPolicyErrors(value: string): string[] {
  if (!value) return []

  const missing: string[] = []

  if (codePointLength(value) < PASSWORD_MIN_LENGTH) {
    missing.push(`at least ${PASSWORD_MIN_LENGTH} characters`)
  }
  if (!UPPERCASE.test(value)) missing.push("one uppercase letter")
  if (!LOWERCASE.test(value)) missing.push("one lowercase letter")
  if (!DIGIT.test(value)) missing.push("one number")

  return missing
}

/**
 * Strength is anchored to the policy, not to a looser guess: anything the
 * server would reject reads Weak, so the meter can never encourage a password
 * the API is about to refuse.
 */
export function passwordStrength(value: string): PasswordStrength | null {
  if (!value) return null
  if (passwordPolicyErrors(value).length) return "Weak"

  return SYMBOL.test(value) || codePointLength(value) >= PASSWORD_MIN_LENGTH + 2
    ? "Strong"
    : "Good"
}
